import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Star, Shield, Truck, Award, ChevronLeft, Phone, Mail, Factory, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import WhatsAppButton from '@/components/WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Bulk Inquiry Form
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    quantity: '',
    message: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} – RSD Accessories B2B`;
      setSelectedImage(product.image_url);
      
      // Add Product Schema Markup for SEO
      const schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.id = 'product-schema';
      schemaScript.innerHTML = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.image_url,
        "sku": product.id,
        "brand": {
          "@type": "Brand",
          "name": "RSD Accessories"
        },
        "manufacturer": {
          "@type": "Organization",
          "name": "RSD Accessories Pvt Ltd",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "B-08, Sector-57",
            "addressLocality": "Noida",
            "postalCode": "201301",
            "addressRegion": "Uttar Pradesh",
            "addressCountry": "IN"
          }
        },
        "countryOfOrigin": "India"
      });
      
      const existingSchema = document.getElementById('product-schema');
      if (existingSchema) existingSchema.remove();
      document.head.appendChild(schemaScript);
      
      return () => {
        const schema = document.getElementById('product-schema');
        if (schema) schema.remove();
      };
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await axios.post(`${API}/corporate-inquiry`, {
        ...inquiryForm,
        product_id: product.id,
        product_name: product.name,
        inquiry_type: 'bulk_quote'
      });
      
      toast.success('Inquiry submitted! Our team will contact you within 24 hours.');
      setShowInquiryForm(false);
      setInquiryForm({ name: '', company: '', email: '', phone: '', quantity: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please call us directly.');
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF4D00]"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-neutral-800 mb-4">Product Not Found</h2>
        <Link to="/shop">
          <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white">Browse Products</Button>
        </Link>
      </div>
    );
  }

  const allImages = [product.image_url, ...(product.additional_images || [])];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to="/shop" 
          className="inline-flex items-center text-neutral-600 hover:text-[#FF4D00] mb-6"
          data-testid="back-to-shop"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="aspect-square bg-neutral-100 rounded-2xl overflow-hidden mb-4">
              <img 
                src={selectedImage} 
                alt={product.name}
                className="w-full h-full object-contain"
                data-testid="product-main-image"
              />
            </div>
            
            {/* Thumbnail Gallery */}
            {allImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(img)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === img ? 'border-[#FF4D00]' : 'border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {/* B2B Badge */}
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              <Factory className="w-4 h-4" />
              B2B Wholesale Only
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4" data-testid="product-name">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center bg-green-100 px-2 py-1 rounded">
                <Star className="w-4 h-4 text-yellow-500 fill-current" />
                <span className="ml-1 font-bold text-green-800">{product.rating}</span>
              </div>
              <span className="text-neutral-500">({product.reviews_count} reviews)</span>
            </div>

            {/* Key Features */}
            <div className="mb-6">
              <h3 className="font-bold text-lg mb-3">Key Features</h3>
              <ul className="space-y-2">
                {product.features?.slice(0, 5).map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* B2B CTA Buttons */}
            <div className="space-y-3 mb-6">
              <Button 
                onClick={() => setShowInquiryForm(true)}
                className="w-full bg-[#FF4D00] hover:bg-[#e64500] text-white py-6 text-lg rounded-xl font-bold"
                data-testid="request-quote-btn"
              >
                <Mail className="w-5 h-5 mr-2" />
                Request Bulk Quote
              </Button>
              
              <div className="grid grid-cols-2 gap-3">
                <a href="tel:+919311211889" className="block">
                  <Button 
                    variant="outline" 
                    className="w-full border-[#FF4D00] text-[#FF4D00] hover:bg-[#FF4D00] hover:text-white py-4 rounded-xl font-semibold"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                </a>
                <a 
                  href={`https://wa.me/919311211889?text=Hi, I'm interested in bulk order for: ${product.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button 
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-4 rounded-xl font-semibold"
                  >
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-neutral-50 rounded-xl mb-6">
              <div className="text-center">
                <Shield className="w-6 h-6 mx-auto mb-1 text-[#FF4D00]" />
                <p className="text-xs text-neutral-600">{product.warranty || '6 Month Warranty'}</p>
              </div>
              <div className="text-center">
                <Truck className="w-6 h-6 mx-auto mb-1 text-[#FF4D00]" />
                <p className="text-xs text-neutral-600">Pan India Delivery</p>
              </div>
              <div className="text-center">
                <Award className="w-6 h-6 mx-auto mb-1 text-[#FF4D00]" />
                <p className="text-xs text-neutral-600">Quality Assured</p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="font-bold text-lg mb-3">Product Description</h3>
              <p className="text-neutral-600 leading-relaxed">{product.description}</p>
            </div>

            {/* Specifications */}
            <div className="mb-6 bg-neutral-50 rounded-xl p-6">
              <h3 className="font-bold text-lg mb-4">Specifications</h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(product.specifications || {}).map(([key, value]) => (
                  <div key={key}>
                    <p className="text-sm text-neutral-500 capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="font-semibold">{value}</p>
                  </div>
                ))}
              </div>
              
              {/* Compatibility Note for Chargers */}
              {product.compatibility_note && (
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-amber-800 font-medium">{product.compatibility_note}</p>
                  </div>
                </div>
              )}
            </div>

            {/* What's Included */}
            {product.includes && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-sm mb-2">📦 Box Contents</h3>
                <p className="text-sm text-neutral-700">{product.includes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Inquiry Modal */}
      {showInquiryForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Request Bulk Quote</h2>
                <button 
                  onClick={() => setShowInquiryForm(false)}
                  className="p-2 hover:bg-neutral-100 rounded-full"
                >
                  ✕
                </button>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-neutral-600">Product:</p>
                <p className="font-semibold">{product.name}</p>
              </div>

              <form onSubmit={handleInquirySubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Your Name *</label>
                    <input
                      type="text"
                      required
                      value={inquiryForm.name}
                      onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-[#FF4D00]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Company Name *</label>
                    <input
                      type="text"
                      required
                      value={inquiryForm.company}
                      onChange={(e) => setInquiryForm({...inquiryForm, company: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-[#FF4D00]"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-[#FF4D00]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone *</label>
                    <input
                      type="tel"
                      required
                      value={inquiryForm.phone}
                      onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-[#FF4D00]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Required Quantity *</label>
                  <select
                    required
                    value={inquiryForm.quantity}
                    onChange={(e) => setInquiryForm({...inquiryForm, quantity: e.target.value})}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-[#FF4D00]"
                  >
                    <option value="">Select quantity range</option>
                    <option value="100-500">100 - 500 units</option>
                    <option value="500-1000">500 - 1,000 units</option>
                    <option value="1000-5000">1,000 - 5,000 units</option>
                    <option value="5000-10000">5,000 - 10,000 units</option>
                    <option value="10000+">10,000+ units</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Additional Requirements</label>
                  <textarea
                    rows={3}
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                    placeholder="Custom branding, packaging requirements, delivery location..."
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:border-[#FF4D00]"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={formLoading}
                  className="w-full bg-[#FF4D00] hover:bg-[#e64500] text-white py-4 rounded-xl font-bold"
                >
                  {formLoading ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              </form>
              
              <p className="text-xs text-neutral-500 text-center mt-4">
                Our team will contact you within 24 hours with the best quote.
              </p>
            </div>
          </div>
        </div>
      )}

      <WhatsAppButton />
    </div>
  );
};

export default ProductDetail;
